/**
 * Absence — fronta žádostí o nepřítomnost a kalendář, kdo kdy chybí.
 *
 * Zůstává vlastní záložkou, nešla do Plánu (otevřená otázka z návrhu).
 * Schvalovací fronta je workflow s vlastními stavy a vlastním publikem;
 * v Plánu by se schovala za přepínač zobrazení na obrazovce, která je
 * o něčem jiném. Rozměr „kdo kdy chybí" Plán pokrývá šrafurou v mřížce.
 *
 * Dvě zobrazení nad jedněmi daty:
 * - **Seznam** — fronta, není vázaná na měsíc. Žádost na příští měsíc musí
 *   být vidět dneska, jinak se schválí pozdě.
 * - **Kalendář** — měsíční mřížka zaměstnanec × den. Tohle dřív chybělo
 *   úplně a je to jediný důvod, proč by sem manažer chodil dobrovolně.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, UserX, Check, Ban, Clock, CheckCircle2, List, CalendarDays, LayoutGrid, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { TYP_CONFIG, STAV_CONFIG, barvaSmen, stripDiacritics, csrf, toLocalDateStr, todayStr } from './spolecne';
import ListToolbar, { PohledPill } from '../../components/layout/ListToolbar';
import Dropdown from '../../components/receptura/Dropdown';
import SidePanel from '../../components/SidePanel';
import UpravitNepritomnost from '../../components/notifikace/UpravitNepritomnost';

const PRAZDNY = { zamestnanec_id: '', od: '', do: '', hodiny: '', typ: 'osobni', poznamka: '' };

const dniMeziLabel = (od, doo) => {
    const n = Math.round((new Date(doo) - new Date(od)) / 86400000) + 1;
    return `${n} ${n === 1 ? 'den' : n < 5 ? 'dny' : 'dní'}`;
};

const pocetDniMezi = (od, doo) => Math.round((new Date(doo) - new Date(od)) / 86400000) + 1;
const fmtH = (h) => `${Number(h).toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} h`;

// `hodiny` jsou za den; u vícedenní nepřítomnosti ukážeme i součet.
// Staré záznamy hodiny nemají — tam zůstane jen počet dní.
const hodinyLabel = (z) => {
    if (z.hodiny == null) return null;
    const n = pocetDniMezi(z.od, z.do);
    return n > 1 ? `${fmtH(z.hodiny)}/den · ${fmtH(z.hodiny * n)} celkem` : fmtH(z.hodiny);
};

export default function Absence({ isDark, glassCard, mutedColor, iStyle, isAdmin, user }) {
    const [zaznamy, setZaznamy] = useState([]);
    const [loading, setLoading] = useState(true);
    const [zamestnanci, setZamestnanci] = useState([]);

    const [pohled, setPohled] = useState('ceka');
    const [hledani, setHledani] = useState('');
    const [zobrazeni, setZobrazeni] = useState('seznam');
    const [mesic, setMesic] = useState(() => {
        const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    // Detail i formulář jedou v inspektoru, ne v pravém sloupci — ten je
    // pomocník (čísla a co nehraje) a formulář by z něj udělal schodůček.
    // Nikdy oboje najednou: `otevriFormular` vybranou žádost zahazuje.
    const [vybrany, setVybrany] = useState(null);
    const [form, setForm] = useState(null);      // null = formulář zavřený
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Čekající žádost, kterou schvalovatel opravuje a rovnou schvaluje —
    // týž formulář jako v notifikaci, jedno upozornění zaměstnanci.
    const [upravujeId, setUpravujeId] = useState(null);

    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 900);
    // Na mobilu jen seznam — mřížka zaměstnanec × den se na výšku telefonu
    // nikdy nevejde tak, aby se z ní dalo číst.
    const kalendarVidet = zobrazeni === 'kalendar' && !isMobile;
    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 900);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    // Horní hrana bloku s daty = horní hrana pomocného sloupce. Inspektor z ní
    // vyjíždí, takže sloupec překryje a lišta nad ním zůstane odkrytá.
    const obsahRef = useRef(null);
    const [inspektorTop, setInspektorTop] = useState(0);
    useEffect(() => {
        const zmer = () => {
            const el = obsahRef.current;
            if (el) setInspektorTop(Math.max(0, Math.round(el.getBoundingClientRect().top)));
        };
        zmer();
        window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, [zobrazeni, pohled, isMobile]);

    const SECTION_COLOR = barvaSmen(isDark);
    const borderColor = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)';
    const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('cs-CZ') : '—');

    const fetchData = useCallback(() => {
        setLoading(true);
        fetch('/api/shifts/nedostupnosti')
            .then(r => r.json())
            .then(d => setZaznamy(Array.isArray(d) ? d : []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        // Admin: všichni; jinak sebe + podřízené (strom si složíme z `nadrizeny_id`).
        fetch('/api/zamestnanci').then(r => r.json()).then(d => {
            const list = Array.isArray(d) ? d : d.data || [];
            if (isAdmin) { setZamestnanci(list); return; }
            const myZamId = user?.zamestnanec_id;
            if (!myZamId) { setZamestnanci([]); return; }
            const ids = new Set([myZamId]);
            const fronta = [myZamId];
            while (fronta.length) {
                const cur = fronta.shift();
                list.filter(z => z.nadrizeny_id === cur).forEach(z => {
                    if (!ids.has(z.id)) { ids.add(z.id); fronta.push(z.id); }
                });
            }
            setZamestnanci(list.filter(z => ids.has(z.id)));
        }).catch(() => {});
    }, [isAdmin, user?.zamestnanec_id]);

    // ── Akce ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true); setError(null);
        const url = editingId ? `/api/shifts/nedostupnosti/${editingId}` : '/api/shifts/nedostupnosti';
        try {
            const res = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({ ...form, zamestnanec_id: form.zamestnanec_id || user?.zamestnanec_id }),
            });
            if (!res.ok) { setError((await res.json().catch(() => ({}))).error || 'Nepodařilo se uložit'); return; }
            setForm(null); setEditingId(null); setVybrany(null);
            fetchData();
        } catch { setError('Chyba sítě'); }
        finally { setSaving(false); }
    };

    const handleAction = async (id, akce) => {
        await fetch(`/api/shifts/nedostupnosti/${id}/${akce}`, { method: 'POST', headers: { 'X-CSRF-TOKEN': csrf() } });
        setVybrany(null);
        fetchData();
    };

    const handleDelete = async (id) => {
        if (!confirm('Opravdu smazat tuhle nepřítomnost?')) return;
        await fetch(`/api/shifts/nedostupnosti/${id}`, { method: 'DELETE', headers: { 'X-CSRF-TOKEN': csrf() } });
        setVybrany(null);
        fetchData();
    };

    const otevriFormular = (z = null) => {
        setEditingId(z?.id ?? null);
        setForm(z
            ? { zamestnanec_id: z.zamestnanec_id || '', od: z.od || '', do: z.do || '', hodiny: z.hodiny ?? '', typ: z.typ || 'osobni', poznamka: z.poznamka || '' }
            : { ...PRAZDNY });
        setVybrany(null);
        setError(null);
    };

    // ── Hledání a pohledy ──
    const hledany = stripDiacritics(hledani);
    const projdeHledanim = (z) => !hledany || stripDiacritics(
        `${z.zamestnanec?.jmeno || ''} ${z.zamestnanec?.prijmeni || ''} ${z.poznamka || ''}`,
    ).includes(hledany);

    const hledane = zaznamy.filter(projdeHledanim);
    const pocty = {
        ceka: hledane.filter(z => z.stav === 'ceka').length,
        schvaleno: hledane.filter(z => z.stav === 'schvaleno').length,
        zamitnuto: hledane.filter(z => z.stav === 'zamitnuto').length,
        vse: hledane.length,
    };
    const seznam = (pohled === 'vse' ? hledane : hledane.filter(z => z.stav === pohled))
        .slice()
        .sort((a, b) => String(b.od).localeCompare(String(a.od)));

    // ── Kalendář: dny × lidi ──
    const [rok, mesicCislo] = mesic.split('-').map(Number);
    const pocetDnu = new Date(rok, mesicCislo, 0).getDate();
    const dnyMesice = Array.from({ length: pocetDnu }, (_, i) => {
        const d = new Date(rok, mesicCislo - 1, i + 1);
        const dow = d.getDay();
        return { datum: toLocalDateStr(d), cislo: i + 1, jeVikend: dow === 0 || dow === 6, jeDnes: toLocalDateStr(d) === todayStr() };
    });
    const prvniDen = dnyMesice[0].datum;
    const posledniDen = dnyMesice[pocetDnu - 1].datum;

    // Zamítnuté se do kalendáře nekreslí — nikdo kvůli nim chybět nebude.
    const vMesici = hledane.filter(z => z.stav !== 'zamitnuto' && z.od <= posledniDen && z.do >= prvniDen);
    const lideVKalendari = [];
    vMesici.forEach(z => {
        const id = z.zamestnanec_id;
        let radek = lideVKalendari.find(r => r.id === id);
        if (!radek) {
            radek = {
                id,
                jmeno: z.zamestnanec ? `${z.zamestnanec.jmeno} ${z.zamestnanec.prijmeni}` : 'Já',
                dny: {},
            };
            lideVKalendari.push(radek);
        }
        dnyMesice.forEach(d => {
            if (d.datum >= z.od && d.datum <= z.do) radek.dny[d.datum] = z;
        });
    });
    lideVKalendari.sort((a, b) => a.jmeno.localeCompare(b.jmeno, 'cs'));

    const posunMesic = (krok) => {
        const d = new Date(rok, mesicCislo - 1 + krok, 1);
        setMesic(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };
    const mesicLabel = new Date(rok, mesicCislo - 1).toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });

    // ── Kousky UI ──
    const pilulka = (id, Ikona, label, upozorneni = false) => (
        <PohledPill key={id} Icon={Ikona} label={label} pocet={pocty[id]}
            aktivni={pohled === id} upozorneni={upozorneni} onClick={() => setPohled(id)}
            isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={isMobile} />
    );

    const prepinacZobrazeni = (
        <div style={{ display: 'flex', gap: 3, padding: 3, borderRadius: 7,
            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--surface-raised)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)'}` }}>
            {[{ id: 'seznam', label: 'Seznam', Icon: List }, { id: 'kalendar', label: 'Kalendář', Icon: LayoutGrid }].map(v => (
                <button key={v.id} onClick={() => setZobrazeni(v.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 6,
                    border: 'none', cursor: 'pointer', fontSize: 11.5, fontWeight: 600,
                    background: zobrazeni === v.id ? `${SECTION_COLOR}20` : 'transparent',
                    color: zobrazeni === v.id ? SECTION_COLOR : mutedColor,
                }}>
                    <v.Icon size={12} />{v.label}
                </button>
            ))}
        </div>
    );

    const navigaceMesice = kalendarVidet && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button onClick={() => posunMesic(-1)} title="Předchozí měsíc" aria-label="Předchozí měsíc"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, display: 'flex' }}>
                <ChevronLeft size={15} />
            </button>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)', minWidth: 104,
                textAlign: 'center', textTransform: 'capitalize' }}>{mesicLabel}</span>
            <button onClick={() => posunMesic(1)} title="Další měsíc" aria-label="Další měsíc"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: 4, display: 'flex' }}>
                <ChevronRight size={15} />
            </button>
        </div>
    );

    const radekSeznamu = (z) => {
        const typ = TYP_CONFIG[z.typ] || TYP_CONFIG.jine;
        const stav = STAV_CONFIG[z.stav] || STAV_CONFIG.ceka;
        const vybrano = vybrany?.id === z.id;
        return (
            <button key={z.id} type="button" onClick={() => { setForm(null); setVybrany(vybrano ? null : z); }}
                style={{
                    ...glassCard, display: 'flex', alignItems: 'center', gap: 11, width: '100%', textAlign: 'left',
                    padding: '10px 13px', borderRadius: 11, cursor: 'pointer',
                    borderLeft: `3px solid ${typ.color}`,
                    outline: vybrano ? `2px solid ${SECTION_COLOR}` : 'none', outlineOffset: -1,
                }}>
                <span style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: typ.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <UserX size={15} style={{ color: typ.color }} />
                </span>
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                            {z.zamestnanec ? `${z.zamestnanec.jmeno} ${z.zamestnanec.prijmeni}` : 'Já'}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 99,
                            background: typ.bg, color: typ.color }}>{typ.label}</span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 99,
                            background: stav.bg, color: stav.color }}>{stav.label}</span>
                    </span>
                    <span style={{ display: 'block', fontSize: 11, color: mutedColor, marginTop: 2 }}>
                        {fmtDate(z.od)} – {fmtDate(z.do)} · {dniMeziLabel(z.od, z.do)}
                        {hodinyLabel(z) ? ` · ${hodinyLabel(z)}` : ''}
                        {z.poznamka ? ` · ${z.poznamka}` : ''}
                    </span>
                </span>
                {isAdmin && z.stav === 'ceka' && (
                    <span style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        <span role="button" tabIndex={0} title="Schválit"
                            onClick={(e) => { e.stopPropagation(); handleAction(z.id, 'schvalit'); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleAction(z.id, 'schvalit'); } }}
                            style={{ padding: 6, borderRadius: 7, background: 'rgba(16,185,129,0.12)', color: '#10B981', display: 'flex', cursor: 'pointer' }}>
                            <Check size={14} />
                        </span>
                        <span role="button" tabIndex={0} title="Zamítnout"
                            onClick={(e) => { e.stopPropagation(); handleAction(z.id, 'zamitnout'); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleAction(z.id, 'zamitnout'); } }}
                            style={{ padding: 6, borderRadius: 7, background: 'rgba(239,68,68,0.12)', color: '#EF4444', display: 'flex', cursor: 'pointer' }}>
                            <Ban size={14} />
                        </span>
                    </span>
                )}
            </button>
        );
    };

    // Kalendář: zaměstnanec × den. Schválené plnou barvou, čekající čárkovaně —
    // plánovač musí poznat, s čím může počítat a co ještě visí.
    const kalendar = (
        <div style={{ ...glassCard, borderRadius: 11, overflow: 'hidden' }}>
            {lideVKalendari.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: mutedColor, fontSize: 13 }}>
                    V {mesicLabel.toLowerCase()} nikdo nechybí.
                </div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11 }}>
                        <thead>
                            <tr>
                                <th style={{ position: 'sticky', left: 0, zIndex: 2, background: isDark ? '#100b18' : '#fff',
                                    textAlign: 'left', padding: '7px 11px', minWidth: 150,
                                    fontSize: 10, fontWeight: 700, color: mutedColor, textTransform: 'uppercase', letterSpacing: 0.5,
                                    boxShadow: `inset 0 -1px 0 ${borderColor}` }}>Zaměstnanec</th>
                                {dnyMesice.map(d => (
                                    <th key={d.datum} style={{
                                        padding: '7px 0', minWidth: 22, textAlign: 'center',
                                        fontSize: 10, fontWeight: 700,
                                        color: d.jeDnes ? SECTION_COLOR : d.jeVikend ? mutedColor : 'var(--text-secondary)',
                                        background: d.jeDnes ? `${SECTION_COLOR}18` : d.jeVikend ? (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)') : 'transparent',
                                        boxShadow: `inset 0 -1px 0 ${borderColor}`,
                                    }}>{d.cislo}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {lideVKalendari.map(r => (
                                <tr key={r.id}>
                                    <td style={{ position: 'sticky', left: 0, zIndex: 2, background: isDark ? '#100b18' : '#fff',
                                        padding: '6px 11px', whiteSpace: 'nowrap', fontSize: 12, fontWeight: 500,
                                        color: 'var(--text-primary)', borderBottom: `1px solid ${borderColor}` }}>{r.jmeno}</td>
                                    {dnyMesice.map(d => {
                                        const z = r.dny[d.datum];
                                        const typ = z ? (TYP_CONFIG[z.typ] || TYP_CONFIG.jine) : null;
                                        const ceka = z?.stav === 'ceka';
                                        return (
                                            <td key={d.datum}
                                                title={z ? `${typ.label} · ${STAV_CONFIG[z.stav]?.label} · ${fmtDate(z.od)} – ${fmtDate(z.do)}${z.hodiny != null ? ` · ${fmtH(z.hodiny)}` : ''}` : undefined}
                                                onClick={z ? () => { setForm(null); setVybrany(z); } : undefined}
                                                style={{
                                                    padding: 0, height: 26, cursor: z ? 'pointer' : 'default',
                                                    borderBottom: `1px solid ${borderColor}`,
                                                    background: d.jeVikend && !z ? (isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)') : 'transparent',
                                                }}>
                                                {z && (
                                                    <span style={{
                                                        display: 'block', height: 16, margin: '0 1px', borderRadius: 3,
                                                        background: ceka ? 'transparent' : typ.color,
                                                        border: ceka ? `1px dashed ${typ.color}` : 'none',
                                                        opacity: ceka ? 0.9 : 0.85,
                                                    }} />
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {lideVKalendari.length > 0 && (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', padding: '9px 12px',
                    borderTop: `1px solid ${borderColor}`, fontSize: 10.5, color: mutedColor }}>
                    {Object.entries(TYP_CONFIG).map(([k, v]) => (
                        <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ width: 11, height: 11, borderRadius: 3, background: v.color }} />{v.label}
                        </span>
                    ))}
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        <span style={{ width: 11, height: 11, borderRadius: 3, border: `1px dashed ${mutedColor}` }} />čeká na schválení
                    </span>
                </div>
            )}
        </div>
    );

    // ── Pravý sloupec: detail nebo formulář ──
    const popisek = { fontSize: 10, fontWeight: 600, color: mutedColor, textTransform: 'uppercase',
        letterSpacing: 0.3, marginBottom: 3, display: 'block' };

    const formularObsah = form && (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
            {editingId && !isAdmin && (
                <div style={{ padding: '8px 10px', borderRadius: 8, fontSize: 11, lineHeight: 1.45,
                    background: 'rgba(245,158,11,0.1)', color: '#F59E0B', border: '1px solid rgba(245,158,11,0.24)' }}>
                    Úprava zruší schválení — žádost půjde znovu do fronty.
                </div>
            )}

            {zamestnanci.length > 1 && (
                <div>
                    <span style={popisek}>Zaměstnanec</span>
                    <Dropdown
                        value={form.zamestnanec_id === '' ? '' : String(form.zamestnanec_id)}
                        onChange={(v) => setForm(f => ({ ...f, zamestnanec_id: v }))}
                        options={zamestnanci.filter(z => z.stav === 'aktivni')
                            .map(z => ({ value: String(z.id), label: z.cele_jmeno || `${z.jmeno} ${z.prijmeni}` }))}
                        placeholder="Vybrat…" isDark={isDark} accent={SECTION_COLOR} height={34}
                    />
                </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={popisek}>Od</span>
                    <input type="date" required value={form.od}
                        onChange={e => setForm(f => ({ ...f, od: e.target.value }))} style={{ ...iStyle, width: '100%' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={popisek}>Do</span>
                    <input type="date" required value={form.do}
                        onChange={e => setForm(f => ({ ...f, do: e.target.value }))} style={{ ...iStyle, width: '100%' }} />
                </div>
            </div>

            <div>
                <span style={popisek}>Hodin za den</span>
                <input type="number" required min="0.5" max="24" step="0.5" inputMode="decimal"
                    value={form.hodiny} placeholder="např. 8"
                    onChange={e => setForm(f => ({ ...f, hodiny: e.target.value }))} style={{ ...iStyle, width: '100%' }} />
                {form.od && form.do && form.do >= form.od && Number(form.hodiny) > 0 && pocetDniMezi(form.od, form.do) > 1 && (
                    <span style={{ display: 'block', fontSize: 11, color: mutedColor, marginTop: 4 }}>
                        Celkem {fmtH(form.hodiny * pocetDniMezi(form.od, form.do))} za {dniMeziLabel(form.od, form.do)}
                    </span>
                )}
            </div>

            <div>
                <span style={popisek}>Typ</span>
                <Dropdown
                    value={form.typ}
                    onChange={(v) => setForm(f => ({ ...f, typ: v }))}
                    options={Object.entries(TYP_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                    isDark={isDark} accent={SECTION_COLOR} height={34}
                />
            </div>

            <div>
                <span style={popisek}>Poznámka</span>
                <input type="text" value={form.poznamka} placeholder="Volitelné"
                    onChange={e => setForm(f => ({ ...f, poznamka: e.target.value }))} style={{ ...iStyle, width: '100%' }} />
            </div>

            {error && <div style={{ fontSize: 11.5, color: '#EF4444' }}>{error}</div>}

            <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" disabled={saving}
                    style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        fontSize: 12.5, fontWeight: 600, background: SECTION_COLOR, color: '#14091f', opacity: saving ? 0.6 : 1 }}>
                    {saving ? 'Ukládám…' : editingId ? 'Uložit změny' : 'Odeslat žádost'}
                </button>
                <button type="button" onClick={() => { setForm(null); setEditingId(null); setError(null); }}
                    style={{ padding: '9px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5,
                        background: 'transparent', color: mutedColor, border: `1px solid ${borderColor}` }}>
                    Zrušit
                </button>
            </div>
        </form>
    );

    const detailObsah = vybrany && (() => {
        const typ = TYP_CONFIG[vybrany.typ] || TYP_CONFIG.jine;
        const stav = STAV_CONFIG[vybrany.stav] || STAV_CONFIG.ceka;
        const radek = (popis, hodnota) => (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
                padding: '7px 0', fontSize: 12, borderBottom: `1px solid ${borderColor}` }}>
                <span style={{ color: mutedColor }}>{popis}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 600, textAlign: 'right' }}>{hodnota}</span>
            </div>
        );
        const tlacitko = (Ikona, label, onClick, barva) => (
            <button onClick={onClick}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, width: '100%',
                    padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                    background: barva ? `${barva}18` : 'transparent', color: barva || mutedColor,
                    border: `1px solid ${barva ? `${barva}40` : borderColor}` }}>
                <Ikona size={14} />{label}
            </button>
        );
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                    <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: typ.bg,
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <UserX size={15} style={{ color: typ.color }} />
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                            {vybrany.zamestnanec ? `${vybrany.zamestnanec.jmeno} ${vybrany.zamestnanec.prijmeni}` : 'Já'}
                        </div>
                        <div style={{ fontSize: 11, color: typ.color, fontWeight: 600 }}>{typ.label}</div>
                    </div>
                </div>

                <div>
                    {radek('Stav', <span style={{ color: stav.color }}>{stav.label}</span>)}
                    {radek('Od', fmtDate(vybrany.od))}
                    {radek('Do', fmtDate(vybrany.do))}
                    {radek('Délka', dniMeziLabel(vybrany.od, vybrany.do))}
                    {radek('Hodiny', hodinyLabel(vybrany) || '—')}
                    {vybrany.schvalil && radek('Rozhodl', `${vybrany.schvalil.jmeno} ${vybrany.schvalil.prijmeni}`)}
                </div>

                {vybrany.poznamka && (
                    <div>
                        <span style={popisek}>Poznámka</span>
                        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.5 }}>{vybrany.poznamka}</div>
                    </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {isAdmin && vybrany.stav === 'ceka' && (<>
                        {tlacitko(Check, 'Schválit', () => handleAction(vybrany.id, 'schvalit'), '#10B981')}
                        {tlacitko(Edit2, 'Upravit a schválit', () => setUpravujeId(vybrany.id), '#10B981')}
                        {tlacitko(Ban, 'Zamítnout', () => handleAction(vybrany.id, 'zamitnout'), '#EF4444')}
                    </>)}
                    {tlacitko(Edit2, 'Upravit', () => otevriFormular(vybrany))}
                    {tlacitko(Trash2, 'Smazat', () => handleDelete(vybrany.id), '#EF4444')}
                </div>
            </div>
        );
    })();

    // ── Pomocný sloupec ──
    // Fronta a kalendář ukazují jednotlivé žádosti; panel ukazuje to, co z nich
    // plyne dohromady — kolik lidí dneska chybí a co se rozpisu rozbije.
    const panelAbsenci = (() => {
        const dnes = todayStr();
        const prekryvaDnes = (z) => z.stav === 'schvaleno' && z.od <= dnes && z.do >= dnes;
        const pryc = zaznamy.filter(prekryvaDnes);

        // Žádost, která začíná dřív, než o ní někdo rozhodne — tichá díra v rozpisu.
        const bezRozhodnuti = zaznamy.filter(z => z.stav === 'ceka' && z.od <= dnes);

        // Dny zobrazeného měsíce, kdy chybí dva a víc lidí najednou.
        const [r, m] = mesic.split('-').map(Number);
        const dnu = new Date(r, m, 0).getDate();
        let kolizi = 0;
        let dnuPrycMesic = 0;
        let hodinPrycMesic = 0;
        for (let i = 1; i <= dnu; i++) {
            const den = `${mesic}-${String(i).padStart(2, '0')}`;
            const tenDen = zaznamy.filter(z => z.stav === 'schvaleno' && z.od <= den && z.do >= den);
            const kolik = tenDen.length;
            if (kolik >= 2) kolizi++;
            dnuPrycMesic += kolik;
            hodinPrycMesic += tenDen.reduce((s, z) => s + (Number(z.hodiny) || 0), 0);
        }

        const nadpisSekce = (text) => (
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                color: mutedColor, margin: '14px 0 5px' }}>{text}</div>
        );
        const radek = (popis, hodnota, barva, titulek) => (
            <div title={titulek} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                gap: 8, padding: '7px 0', borderBottom: `1px solid ${borderColor}` }}>
                <span style={{ fontSize: 12, color: mutedColor }}>{popis}</span>
                <span style={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                    color: barva || 'var(--text-primary)' }}>{hodnota}</span>
            </div>
        );
        const upozorneni = (barva, titulek, detail) => (
            <div style={{ padding: '8px 10px', borderRadius: 9, marginTop: 7, background: `${barva}14`,
                border: `1px solid ${barva}38`, fontSize: 11.5, lineHeight: 1.45, color: barva }}>
                <b style={{ display: 'block', marginBottom: 1 }}>{titulek}</b>{detail}
            </div>
        );

        return (
            <div style={{ ...glassCard, borderRadius: 12, padding: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: pryc.length ? '#F59E0B' : mutedColor }} />
                    <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: mutedColor }}>
                        Dnes chybí · {pryc.length}
                    </span>
                </div>
                {pryc.length === 0 ? (
                    <div style={{ fontSize: 11.5, color: mutedColor, lineHeight: 1.5, padding: '5px 0' }}>Dneska je tým kompletní.</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, padding: '5px 0' }}>
                        {pryc.map(z => {
                            const t = TYP_CONFIG[z.typ] || TYP_CONFIG.jine;
                            return (
                                <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 7,
                                    fontSize: 12, color: 'var(--text-primary)' }}>
                                    <span style={{ width: 7, height: 7, borderRadius: 2, background: t.color, flexShrink: 0 }} />
                                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {z.zamestnanec ? `${z.zamestnanec.jmeno} ${z.zamestnanec.prijmeni}` : 'Já'}
                                    </span>
                                    <span style={{ fontSize: 10.5, color: t.color, flexShrink: 0 }}>{t.label}</span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {nadpisSekce(mesicLabel)}
                {radek('Čeká', pocty.ceka || '–', pocty.ceka ? '#F59E0B' : null)}
                {radek('Schválené', pocty.schvaleno || '–')}
                {radek('Člověkodnů pryč', dnuPrycMesic || '–', null,
                    'Součet dnů schválené nepřítomnosti přes všechny lidi')}
                {radek('Hodin pryč', hodinPrycMesic ? fmtH(hodinPrycMesic) : '–', null,
                    'Součet hodin schválené nepřítomnosti v měsíci (záznamy bez hodin se nepočítají)')}

                {(bezRozhodnuti.length > 0 || kolizi > 0) && (<>
                    {nadpisSekce('K dořešení')}
                    {bezRozhodnuti.length > 0 && upozorneni('#EF4444',
                        `Běží bez rozhodnutí · ${bezRozhodnuti.length}`,
                        'nepřítomnost už začala a pořád čeká ve frontě')}
                    {kolizi > 0 && upozorneni('#F59E0B',
                        `Dny s dvěma a více pryč · ${kolizi}`,
                        'v těch dnech je rozpis nejtenčí — mrkni na kalendář')}
                </>)}
            </div>
        );
    })();

    return (
        <div>
            <div style={{ marginBottom: 12 }}>
                <ListToolbar
                    siroky={!isMobile} kompaktni={isMobile} sklo
                    isDark={isDark} mutedColor={mutedColor} inputStyle={iStyle} barva={SECTION_COLOR}
                    hledani={{
                        value: hledani, onChange: setHledani,
                        placeholder: 'Hledat jméno nebo poznámku…', placeholderKratky: 'Hledat…',
                    }}
                    akce={[{
                        id: 'nova', label: 'Nepřítomnost', Icon: Plus, primarni: true,
                        title: 'Nová nepřítomnost',
                        onClick: () => otevriFormular(null),
                    }]}
                    pohled={(<>
                        {pilulka('ceka', Clock, 'Čeká', pocty.ceka > 0)}
                        {pilulka('schvaleno', CheckCircle2, 'Schválené')}
                        {pilulka('zamitnuto', Ban, 'Zamítnuté')}
                        {pilulka('vse', CalendarDays, 'Vše')}
                    </>)}
                    pohledVpravo={isMobile ? null : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {navigaceMesice}{prepinacZobrazeni}
                        </div>
                    )}
                    pod={null}
                />
            </div>

            {/* Pohled se v kalendáři používá jen na zúžení podle stavu — sám kalendář
                je vždycky nad celým měsícem, protože „kdo chybí" nemá smysl filtrovat
                na jednu žádost. */}
            <div ref={obsahRef} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    {loading ? (
                        <div style={{ padding: 26, textAlign: 'center', color: mutedColor, fontSize: 13 }}>Načítám…</div>
                    ) : kalendarVidet ? kalendar : seznam.length === 0 ? (
                        <div style={{ padding: 30, textAlign: 'center' }}>
                            <UserX size={30} style={{ color: mutedColor, opacity: 0.4, margin: '0 auto 8px' }} />
                            <div style={{ fontSize: 13, color: mutedColor }}>
                                {pohled === 'ceka' ? 'Žádná žádost nečeká na rozhodnutí.' : 'Žádné záznamy nepřítomnosti.'}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {seznam.map(radekSeznamu)}
                        </div>
                    )}
                </div>

                {!isMobile && (
                    <div style={{ width: 292, flexShrink: 0 }}>{panelAbsenci}</div>
                )}
            </div>

            {/* Na mobilu se sloupec nevejde — panel klízne pod seznam. */}
            {isMobile && <div style={{ marginTop: 12 }}>{panelAbsenci}</div>}

            {/* Formulář i detail vyjedou zprava přes pomocný sloupec — fronta
                vedle zůstane vidět, takže se dá rozhodovat žádost po žádosti.
                Na mobilu je to modál přes celou plochu. */}
            {form && (
                <SidePanel open onClose={() => { setForm(null); setEditingId(null); setError(null); }}
                    title={editingId ? 'Upravit nepřítomnost' : 'Nová nepřítomnost'}
                    isDark={isDark} isMobile={isMobile} width={400} modal={false}
                    accent={SECTION_COLOR} topOffset={inspektorTop}>
                    <div style={{ padding: 16 }}>{formularObsah}</div>
                </SidePanel>
            )}

            {vybrany && !form && (
                <SidePanel open onClose={() => setVybrany(null)} title="Nepřítomnost"
                    isDark={isDark} isMobile={isMobile} width={400} modal={false}
                    accent={SECTION_COLOR} topOffset={inspektorTop}>
                    <div style={{ padding: 16 }}>{detailObsah}</div>
                </SidePanel>
            )}

            {upravujeId && (
                <UpravitNepritomnost nedostupnostId={upravujeId} isDark={isDark}
                    onClose={() => setUpravujeId(null)}
                    onHotovo={() => { setUpravujeId(null); setVybrany(null); fetchData(); window.dispatchEvent(new Event('notifikace-changed')); }} />
            )}
        </div>
    );
}
